import type { IVtsExhibitor } from '../Admin/Exhibitors/Exhibitors';
import type { BoothPalette } from './brandPalette';
import type { BoothEventName } from './events';

/**
 * Contract between the booth overlay shell and its panels. The shell mounts one panel per
 * visible tab (inactive ones are hidden, not unmounted) so every panel can report its badge.
 */

export type BoothTabId =
  | 'assortment'
  | 'suggested'
  | 'highlights'
  | 'samples'
  | 'media'
  | 'feedback'
  | 'contact';

/** Tracks an event for the exhibitor of the open booth. */
export type BoothTrack = (
  event: BoothEventName,
  payload?: Record<string, unknown>
) => void;

/** Badge count per tab; null or missing means "no badge". */
export type BoothBadges = Partial<Record<BoothTabId, number | null>>;

export const BOOTH_TAB_LABELS: Record<BoothTabId, string> = {
  assortment: 'Assortment',
  suggested: 'Suggested for you',
  highlights: 'Highlights & new',
  samples: 'Samples',
  media: 'Media & downloads',
  feedback: 'Give feedback',
  contact: 'Contact',
};

export interface BoothPanelProps {
  exhibitor: IVtsExhibitor;
  tab: BoothTabId;
  /** True while this panel is the visible one. */
  active: boolean;
  palette: BoothPalette;
  /** Reports the badge of a tab; null hides the badge. Stable across renders. */
  onBadge: (tab: BoothTabId, count: number | null) => void;
  /** Tracks an event for this exhibitor. Stable across renders. */
  track: BoothTrack;
}

/** DOM id of a tab button, referenced by its panel. */
export function boothTabDomId(tab: BoothTabId): string {
  return 'booth-tab-' + tab;
}

/** DOM id of a tab panel, referenced by its tab button. */
export function boothPanelDomId(tab: BoothTabId): string {
  return 'booth-panel-' + tab;
}

/**
 * True when the booth has the Highlights & new tab: highlights or new products exist. Every
 * package gets the tab (Santi, 02.11.2026); the briefing only fixes the slot count per package.
 */
export function hasHighlightsTab(exhibitor: IVtsExhibitor): boolean {
  return !!exhibitor.has_highlights || !!exhibitor.has_new_product_attributes;
}

/**
 * Tabs of a booth in display order. Assortment, Give feedback and Contact are always present,
 * like Place order and Give feedback in the old popup.
 */
export function tabsForExhibitor(
  exhibitor: IVtsExhibitor,
  badges: BoothBadges
): BoothTabId[] {
  const tabs: BoothTabId[] = ['assortment'];

  if ((badges.suggested ?? 0) > 0) {
    tabs.push('suggested');
  }
  if (hasHighlightsTab(exhibitor)) {
    tabs.push('highlights');
  }
  if (exhibitor.has_samples) {
    tabs.push('samples');
  }
  if (
    exhibitor.has_media_files ||
    exhibitor.complete_media_kit_status === 'ready'
  ) {
    tabs.push('media');
  }
  tabs.push('feedback');
  tabs.push('contact');

  return tabs;
}

const POPUP_PARAM_TABS: Record<string, BoothTabId> = {
  video: 'assortment',
  order: 'assortment',
  products: 'assortment',
  awards: 'assortment',
  assortment: 'assortment',
  highlight: 'highlights',
  new: 'highlights',
  highlights: 'highlights',
  samples: 'samples',
  media: 'media',
  feedback: 'feedback',
  contact: 'contact',
  suggested: 'suggested',
};

/**
 * Maps the `?popup=` deep-link value (including the values of the old popup) to a tab.
 * Unknown or missing values open Assortment.
 */
export function tabFromPopupParam(value?: string | null): BoothTabId {
  const key = (value ?? '').trim().toLowerCase();

  return Object.prototype.hasOwnProperty.call(POPUP_PARAM_TABS, key)
    ? POPUP_PARAM_TABS[key]
    : 'assortment';
}
